/**
 * `#top` — `template.html` dòng 605–775.
 *
 * KHÔNG có `title` và `lead` ở đây: h1 và lead của hero dùng lại `page.home.heading` /
 * `page.home.body`. Hai chuỗi đó khớp TỪNG CHỮ với thiết kế (dòng 610 và 611), và `types.ts`
 * vốn đã ép mọi `PageId` phải có chúng. Hai nguồn cho cùng một câu là cách chắc chắn nhất để
 * chúng lệch nhau.
 *
 * `mock.*` là chữ NẰM TRONG ảnh chụp giao diện sản phẩm. Nó vẫn phải song ngữ vì người đọc nhìn
 * thấy, nhưng nó không phải nội dung marketing — đừng viết lại cho hay hơn thiết kế.
 */

/**
 * Kiểu khai TẠI ĐÂY thay vì để TypeScript suy ra `string`.
 *
 * Nếu để suy ra, `tint` thành `string` và chỗ dùng (`MockAvatar` ở `packages/ui`) phải ép kiểu —
 * mà ép kiểu là chỗ một giá trị gõ sai đi thẳng tới production. Khai union ở nguồn thì gõ sai là
 * lỗi biên dịch ngay trong file này.
 *
 * KHÔNG import kiểu từ `@nexa/ui`: chiều phụ thuộc đi một chiều từ app xuống packages, không có
 * cạnh nào từ content sang ui. Trùng tên union là chủ ý, và `MockAvatarProps` nhận đúng nó.
 *
 * (Tránh cả ký tự mũi tên U+2192 trong comment: cổng `check-fonts.mjs` đọc TOÀN VĂN file copy để
 * tính tập glyph cần subset, nên một mũi tên trong comment cũng đòi glyph mà font không có.)
 */
interface HeroMessage {
  readonly author: string;
  readonly time: string;
  readonly initials: string;
  readonly tint: 'brand' | 'success' | 'warn' | 'violet';
  readonly text: string;
}

const messages: readonly HeroMessage[] = [
  {
    author: 'Maya C.',
    time: '09:24',
    initials: 'MC',
    tint: 'warn',
    text: 'Bộ token v2 đã xong — mình tách radius ra scale riêng nhé?',
  },
  {
    author: 'Jordan P.',
    time: '09:26',
    initials: 'JP',
    tint: 'brand',
    text: 'Đồng ý. Mình mở topic riêng để channel chính đỡ loãng.',
  },
  {
    author: 'Ngọc L.',
    time: '09:31',
    initials: 'NL',
    tint: 'success',
    text: '@Quill tóm tắt thread này vào release note giúp mình.',
  },
];

/**
 * Một dòng trong danh sách channel của sidebar mock.
 *
 * `icon` là tên `data-lucide` của thiết kế — trùng đúng khoá trong registry icon của
 * `packages/ui`. Khai union ở đây để gõ sai một tên là lỗi biên dịch, thay vì một icon biến mất
 * lặng lẽ lúc chạy.
 *
 * `state`: `active` = channel đang mở (nền brand) · `nested` = topic con, thụt vào ·
 * `idle` = còn lại. Ba hình thức này ĐO ĐƯỢC ở dòng 656–665, không phải thang do ai đó đặt ra.
 *
 * (Chữ trong comment cũng tốn glyph: cổng `check-fonts.mjs` đọc TOÀN VĂN file này để tính tập
 * subset. Một từ có dấu lạ trong chú thích sẽ kéo theo cả một glyph vào font production.)
 */
interface SidebarChannel {
  readonly name: string;
  readonly icon: 'hash' | 'corner-down-right' | 'mic' | 'lock';
  readonly state: 'active' | 'nested' | 'idle';
  readonly badge?: string;
  readonly tag?: string;
}

interface SidebarAgent {
  readonly name: string;
  readonly initials: string;
  readonly tint: 'brand' | 'violet';
}

const channels: readonly SidebarChannel[] = [
  { name: 'design-system', icon: 'hash', badge: '4', state: 'active' },
  { name: 'tokens-v2', icon: 'corner-down-right', state: 'nested' },
  { name: 'design-standup', icon: 'mic', tag: 'LIVE', state: 'nested' },
  { name: 'q2-launch', icon: 'hash', badge: '7', state: 'idle' },
  { name: 'leadership', icon: 'lock', state: 'idle' },
  { name: 'sales-pipeline', icon: 'hash', state: 'idle' },
];

const agents: readonly SidebarAgent[] = [
  { name: 'Quill Analyst', initials: 'QA', tint: 'brand' },
  { name: 'Atlas DevOps', initials: 'AD', tint: 'violet' },
];

export const hero = {
  badge: 'Nexa — sản phẩm của NewEra Inc.',
  ctaPrimary: 'Dùng thử miễn phí',
  ctaSecondary: 'Đặt lịch demo',
  note: 'Web + Mobile · Song ngữ Việt–Anh · Có bản triển khai on-premise',
  /**
   * Nhãn cho 3 nút chấm điều khiển. Thiết kế để 3 `<span>` trơ không bấm được (dòng 1409); bản
   * này biến chúng thành `<button>` để đạt WCAG 2.2.2, nên phải có nhãn cho screen reader.
   */
  paneLabels: ['Xem pane chat', 'Xem pane kanban', 'Xem pane trợ lý AI'],
  mock: {
    urlBar: 'app.nexa.vn/newera-inc',
    workspace: 'NewEra.Inc',
    jumpTo: 'Nhảy tới…',
    groupChannels: 'CHANNELS',
    groupAgents: 'AI AGENTS',
    sidebar: { channels, agents },
    chat: {
      channel: 'design-system',
      members: '24 thành viên',
      topics: ['tokens-v2', 'icon-audit', 'handoff'],
      messages,
      composer: 'Nhắn vào #design-system',
    },
    kanban: {
      title: 'Q2 Launch — Atlas 4.0',
      sprintBadge: 'SPRINT 14',
      deadline: 'Còn 6 ngày',
      columns: [
        {
          title: 'CẦN LÀM · 4',
          cards: ['Soạn runbook rollback', 'Slide readiness cho board', 'Bảng QA sign-off'],
        },
        { title: 'ĐANG LÀM · 3', cards: ['Báo cáo launch readiness Q2', 'Trang pricing v2'] },
        { title: 'CHỜ REVIEW · 2', cards: ['Script migration'] },
        { title: 'HOÀN TẤT · 11', cards: ['Design QA pass'] },
      ],
      priority: 'HIGH',
      assignee: 'JP',
    },
    agent: {
      name: 'Quill Analyst',
      initials: 'QA',
      tools: 'SQL · Snowflake · Drive',
      ask: 'So sánh pipeline Q2 với mục tiêu và chỉ ra rủi ro.',
      reading: 'Đang đọc 3 nguồn',
      sources: ['Snowflake · ANALYTICS.PROD', 'q2-forecast.xlsx', 'Launch Q2 (shared)'],
      answer:
        'Pipeline đang đạt 86% mục tiêu. Hai rủi ro: nhóm deal enterprise trượt khỏi cửa sổ sprint, và còn 3 blocker chưa đóng ở Atlas 4.0.',
      actions: ['Tạo task', 'Đăng vào #q2-launch'],
    },
  },
};
